import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const extractValidIds = <T extends { id?: number }>(itemsList: T[]): Set<number> => {
  const allIds = itemsList.map((i) => i.id);
  const validIds = allIds.filter((id): id is number => id !== undefined);
  return new Set(validIds);
};

export function useAnimatedItems<T extends { id?: number }>(
  items: T[],
  duration = 300
): { 
  displayItems: T[]; 
  currentIds: Set<number>;
  isExiting: (item: T) => boolean;
} {
  const [displayItems, setDisplayItems] = useState<T[]>(items);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const prevItemsRef = useRef(items);

  const currentIds = useMemo(() => extractValidIds(items), [items]);

  useEffect(() => {
    const prevIds = extractValidIds(prevItemsRef.current);
    const prevIdsArray = Array.from(prevIds);
    const hasRemoved = prevIdsArray.some((id) => !currentIds.has(id));

    if (hasRemoved) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setDisplayItems(items);
      }, duration);
    } else {
      setDisplayItems(items);
    }

    prevItemsRef.current = items;

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [items, duration, currentIds]);

  const isExiting = useMemo(() => {
    return (item: T): boolean => {
      if (!item.id) return false;
      return !currentIds.has(item.id);
    };
  }, [currentIds]);

  return { displayItems, currentIds, isExiting };
}
